import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  PencilIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  PlusIcon,
  PhotoIcon,
  CheckCircleIcon,
  ClockIcon,
  CalendarIcon,
  TrophyIcon,
} from '@heroicons/react/24/outline';
import PhotoGalleryModal from './PhotoGalleryModal';
import { supabase } from '../lib/supabase';
import { toast } from 'react-hot-toast';
import type { ContentItem, ContentItemType, PhotoCategory } from '../types/content';
import PublishControl from './PublishControl';

interface ContentSection {
  id: string;
  title: string;
  icon: any;
  items: ContentItem[];
}

interface Tournament {
  id: string;
  name: string;
  start_date: string;
  status: string;
}

const initialSections = [
  {
    title: 'Gallery',
    icon: PhotoIcon,
  }
];

export default function GalleryContentContainer() {
  const [expandedSection, setExpandedSection] = useState<string | null>(null);
  const [sections, setSections] = useState<ContentSection[]>([]);
  const [tournaments, setTournaments] = useState<Tournament[]>([]);
  const [filterTournamentId, setFilterTournamentId] = useState<string>('');
  const [isPhotoModalOpen, setIsPhotoModalOpen] = useState(false);
  const [selectedGalleryItem, setSelectedGalleryItem] = useState<ContentItem | null>(null);
  const [selectedSection, setSelectedSection] = useState<string>('');
  const [pendingTournamentId, setPendingTournamentId] = useState<string>('');
  const [pendingGalleryFolder, setPendingGalleryFolder] = useState<string>('gallery');
  const [pendingGalleryTitle, setPendingGalleryTitle] = useState<string>('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTournaments();
    fetchContent();
  }, []);

  const fetchTournaments = async () => {
    try {
      const { data } = await supabase
        .from('tournaments')
        .select('id, name, start_date, status')
        .order('start_date', { ascending: false });
      setTournaments(data || []);
    } catch {}
  };

  const fetchContent = async () => {
    try {
      setLoading(true);

      for (const section of initialSections) {
        const { data: existingSection, error: checkError } = await supabase
          .from('content_sections')
          .select('id')
          .eq('page', 'history')
          .eq('title', section.title)
          .maybeSingle();

        if (checkError) throw checkError;

        if (!existingSection) {
          const { error: insertError } = await supabase
            .from('content_sections')
            .insert([{
              page: 'history',
              title: section.title,
              icon: section.icon.name
            }]);

          if (insertError) throw insertError;
        }
      }

      const { data: dbSections, error } = await supabase
        .from('content_sections')
        .select(`
          *,
          content_items (*)
        `)
        .eq('page', 'history')
        .eq('title', 'Gallery');

      if (error) throw error;

      if (dbSections) {
        const processedSections = dbSections.map(dbSection => {
          const matchingInitial = initialSections.find(s => s.title === dbSection.title);
          return {
            id: dbSection.id,
            title: dbSection.title,
            icon: matchingInitial?.icon || PhotoIcon,
            items: dbSection.content_items?.map((item: any) => ({
              ...item,
              lastUpdated: new Date(item.updated_at || Date.now()).toISOString().split('T')[0]
            })) || []
          };
        });
        setSections(processedSections);

        if (!expandedSection && processedSections.length > 0) {
          setExpandedSection(processedSections[0].id);
        }
      }
    } catch (err) {
      console.error('Error fetching content:', err);
      toast.error('Failed to load content');
    } finally {
      setLoading(false);
    }
  };

  const toggleSection = (sectionId: string) => {
    setExpandedSection(expandedSection === sectionId ? null : sectionId);
  };

  const handlePhotoGalleryEdit = (item: ContentItem) => {
    setSelectedGalleryItem(item);
    setSelectedSection(item.section_id || '');
    setPendingTournamentId((item as any).tournament_id || '');
    setPendingGalleryFolder(slugifyTitle(item.title) || 'gallery');
    setPendingGalleryTitle(item.title);
    setIsPhotoModalOpen(true);
  };

  const handleAddClick = (sectionId: string) => {
    setSelectedSection(sectionId);
    setSelectedGalleryItem(null);
    setPendingTournamentId(filterTournamentId);
    setPendingGalleryFolder('gallery');
    setPendingGalleryTitle('');
    setIsPhotoModalOpen(true);
  };

  const slugifyTitle = (title: string): string =>
    title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

  const handleToggleStatus = async (item: ContentItem) => {
    try {
      const newStatus = item.status === 'Published' ? 'Draft' : 'Published';
      const { error } = await supabase
        .from('content_items')
        .update({ status: newStatus })
        .eq('id', item.id);
      if (error) throw error;
      await fetchContent();
      toast.success(`Status changed to ${newStatus}`);
    } catch (error: any) {
      console.error('Error updating status:', error);
      toast.error('Failed to update status');
    }
  };

  const handlePhotoGallerySave = async (categories: PhotoCategory[], tournamentId: string | null = null, galleryTitle: string = '') => {
    try {
      const title = galleryTitle.trim();
      if (!title) {
        toast.error('Gallery title is required');
        return;
      }

      const itemId: string | undefined = selectedGalleryItem?.id;

      const itemData: any = {
        title,
        type: 'gallery' as ContentItemType,
        content: JSON.stringify(categories),
        status: selectedGalleryItem?.status || 'Draft',
        section_id: selectedSection,
        tournament_id: tournamentId,
      };

      let error;
      if (itemId) {
        const result = await supabase
          .from('content_items')
          .update(itemData)
          .eq('id', itemId)
          .select();
        error = result.error;
      } else {
        const { data: dbConflict } = await supabase
          .from('content_items')
          .select('id')
          .eq('section_id', selectedSection)
          .eq('title', title)
          .maybeSingle();

        if (dbConflict) {
          toast.error(`A gallery named "${title}" already exists. Please use a different title.`);
          return;
        }

        const result = await supabase
          .from('content_items')
          .insert([itemData])
          .select();
        error = result.error;
      }

      if (error) throw error;

      await fetchContent();
      toast.success('Gallery saved successfully');
      setIsPhotoModalOpen(false);
      setSelectedGalleryItem(null);
    } catch (error: any) {
      console.error('Error saving gallery:', error);
      toast.error(error.message || 'Failed to save gallery');
    }
  };

  const renderPreview = (item: ContentItem) => {
    try {
      if (item.type !== 'gallery') {
        return <p className="text-sm text-gray-600">Not a gallery item</p>;
      }

      let content: PhotoCategory[] = [];

      if (typeof item.content === 'string') {
        try {
          content = JSON.parse(item.content);
        } catch (e) {
          return <p className="text-sm text-red-600">Invalid gallery data</p>;
        }
      } else {
        content = item.content as unknown as PhotoCategory[];
      }

      if (!Array.isArray(content)) {
        content = [content];
      }

      if (content.length === 0 || !content[0]?.photos?.length) {
        return <p className="text-sm text-gray-600">No photos available</p>;
      }

      return (
        <div className="mt-2 grid grid-cols-3 gap-2">
          {content[0].photos.slice(0, 3).map((photo, index) => (
            <div key={index} className="aspect-square rounded-lg overflow-hidden">
              <img
                src={photo.url}
                alt={photo.caption || ''}
                className="w-full h-full object-cover"
                loading="lazy"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.onerror = null;
                  target.src = "https://images.unsplash.com/photo-1587174486073-ae5e5cff23aa";
                }}
              />
            </div>
          ))}
          {content[0].photos.length > 3 && (
            <div className="absolute bottom-2 right-2 bg-black/70 text-white text-xs px-2 py-1 rounded-full">
              +{content[0].photos.length - 3} more
            </div>
          )}
        </div>
      );
    } catch (error) {
      return <p className="text-sm text-red-600">Error displaying preview</p>;
    }
  };

  const getFilteredItems = (items: ContentItem[]) => {
    if (!filterTournamentId) return items;
    return items.filter((item: any) => item.tournament_id === filterTournamentId);
  };

  const getTournamentName = (item: ContentItem) => {
    const tid = (item as any).tournament_id;
    if (!tid) return null;
    return tournaments.find(t => t.id === tid)?.name || null;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-golf-orange border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PublishControl pageKey="gallery" pageTitle="Gallery" />

      {sections.map((section) => {
        const filteredItems = getFilteredItems(section.items);
        return (
          <motion.div
            key={section.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden"
          >
            <div
              className="flex items-center justify-between p-6 cursor-pointer"
              onClick={() => toggleSection(section.id)}
            >
              <div className="flex items-center space-x-4">
                <div className="bg-golf-orange/10 p-3 rounded-xl">
                  <section.icon className="w-6 h-6 text-golf-orange" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-golf-gray">{section.title}</h3>
                  <p className="text-sm text-gray-500">
                    {filteredItems.length} {filteredItems.length !== section.items.length ? `of ${section.items.length} ` : ''}galleries
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2" onClick={e => e.stopPropagation()}>
                <select
                  value={filterTournamentId}
                  onChange={e => setFilterTournamentId(e.target.value)}
                  className="text-sm rounded-lg border-gray-300 focus:border-golf-green focus:ring-golf-green py-1.5 pr-8"
                >
                  <option value="">All Tournaments</option>
                  {tournaments.map(t => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </select>
                <button
                  onClick={() => handleAddClick(section.id)}
                  className="p-2 text-golf-orange hover:bg-golf-orange/10 rounded-lg transition-colors"
                >
                  <PlusIcon className="w-5 h-5" />
                </button>
                {expandedSection === section.id ? (
                  <ChevronUpIcon className="w-5 h-5 text-gray-400" onClick={() => toggleSection(section.id)} />
                ) : (
                  <ChevronDownIcon className="w-5 h-5 text-gray-400" onClick={() => toggleSection(section.id)} />
                )}
              </div>
            </div>

            {expandedSection === section.id && (
              <div className="border-t border-gray-200">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-gray-50 border-b border-gray-200">
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Title</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Preview</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tournament</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Updated</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {filteredItems.length === 0 ? (
                        <tr>
                          <td colSpan={6} className="px-6 py-10 text-center text-sm text-gray-400">
                            No galleries found{filterTournamentId ? ' for the selected tournament' : ''}
                          </td>
                        </tr>
                      ) : (
                        filteredItems.map((item) => {
                          const tournamentName = getTournamentName(item);
                          return (
                            <tr key={item.id} className="hover:bg-gray-50">
                              <td className="px-6 py-4">
                                <div className="text-sm font-medium text-golf-gray">{item.title}</div>
                                <div className="text-xs text-gray-500 capitalize">{item.type}</div>
                              </td>
                              <td className="px-6 py-4 relative">
                                {renderPreview(item)}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                {tournamentName ? (
                                  <span className="inline-flex items-center gap-1 text-sm text-gray-700">
                                    <TrophyIcon className="w-4 h-4 text-golf-orange flex-shrink-0" />
                                    <span className="truncate max-w-[140px]">{tournamentName}</span>
                                  </span>
                                ) : (
                                  <span className="text-xs text-gray-400 italic">Unassigned</span>
                                )}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="flex items-center gap-1 text-sm text-gray-500">
                                  <CalendarIcon className="w-4 h-4" />
                                  {item.lastUpdated || 'N/A'}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <button
                                  onClick={(e) => { e.stopPropagation(); handleToggleStatus(item); }}
                                  className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-semibold rounded-full cursor-pointer transition-opacity hover:opacity-80 ${
                                    item.status === 'Published'
                                      ? 'bg-green-100 text-green-800'
                                      : 'bg-yellow-100 text-yellow-800'
                                  }`}
                                  title={`Click to ${item.status === 'Published' ? 'unpublish' : 'publish'}`}
                                >
                                  {item.status === 'Published' ? (
                                    <CheckCircleIcon className="w-4 h-4" />
                                  ) : (
                                    <ClockIcon className="w-4 h-4" />
                                  )}
                                  {item.status}
                                </button>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                <div className="flex justify-end space-x-2">
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handlePhotoGalleryEdit(item);
                                    }}
                                    className="p-2 text-blue-600 hover:text-blue-900 hover:bg-blue-50 rounded-lg transition-colors"
                                  >
                                    <PencilIcon className="w-5 h-5" />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </motion.div>
        );
      })}

      <PhotoGalleryModal
        isOpen={isPhotoModalOpen}
        onClose={() => {
          setIsPhotoModalOpen(false);
          setSelectedGalleryItem(null);
        }}
        onSave={(categories, tournamentId, galleryTitle) => {
          setPendingGalleryFolder(slugifyTitle(galleryTitle) || 'gallery');
          handlePhotoGallerySave(categories, tournamentId, galleryTitle);
        }}
        initialCategories={selectedGalleryItem && typeof selectedGalleryItem.content === 'string'
          ? JSON.parse(selectedGalleryItem.content)
          : selectedGalleryItem?.content
            ? selectedGalleryItem.content as unknown as PhotoCategory[]
            : []
        }
        initialTournamentId={pendingTournamentId}
        initialTitle={pendingGalleryTitle}
        onTournamentChange={setPendingTournamentId}
        galleryFolder={pendingGalleryFolder}
      />

    </div>
  );
}

